/**
 * Scroll Announcement Bar JavaScript
 * File: assets/scroll-announcement-bar.js
 * 
 * For Ashluxe Shopify Store
 * Handles scroll announcement bar functionality
 */

class ScrollAnnouncementBar {
  constructor() {
    this.scrollAnnouncementBar = null;
    
    this.init();
  }

  /**
   * Initialize the scroll announcement bar
   */
  init() {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setup());
    } else {
      this.setup();
    }
  }

  /**
   * Setup scroll announcement bar functionality
   */
  setup() {
    this.scrollAnnouncementBar = document.getElementById('scroll-announcement-bar');
    
    if (!this.scrollAnnouncementBar) {
      console.log('Scroll announcement bar not found');
      return;
    }

    // Get settings from CSS variables
    const settings = this.getSettings();
    
    // Handle positioning and visibility
    this.handlePositioning(settings);
    
    // Setup event listeners
    this.setupEventListeners();
    
    // Handle gradient background
    this.setupGradientBackground();
    
    console.log('Scroll announcement bar initialized');
  }

  /**
   * Get settings from CSS variables
   */
  getSettings() {
    const rootStyles = getComputedStyle(document.documentElement);
    
    return {
      enabled: rootStyles.getPropertyValue('--scroll-announcement-enabled').trim() === 'true',
      showOnAllPages: rootStyles.getPropertyValue('--scroll-announcement-show-all-pages').trim() === 'true',
      sticky: rootStyles.getPropertyValue('--scroll-announcement-sticky').trim() === 'true'
    };
  }

  /**
   * Handle positioning and visibility based on settings
   */
  handlePositioning(settings) {
    if (!this.scrollAnnouncementBar || !settings.enabled) {
      this.hideBar();
      return;
    }

    // Handle page visibility first
    if (!this.shouldShowOnCurrentPage(settings.showOnAllPages)) {
      this.hideBar();
      return;
    }

    // Show the bar
    this.scrollAnnouncementBar.style.display = '';

    // Handle positioning independently
    if (settings.sticky) {
      this.applyStickyPositioning();
    } else {
      this.applyStaticPositioning();
    }
  }

  /**
   * Check if bar should show on current page
   */
  shouldShowOnCurrentPage(showOnAllPages) {
    if (showOnAllPages) {
      return true; // Show on all pages
    }
    
    // Only show on homepage when "show on all pages" is off
    const currentPath = window.location.pathname;
    return currentPath === '/' || currentPath === '/index' || currentPath === '';
  }

  /**
   * Apply sticky positioning at the top
   */
  applyStickyPositioning() {
    this.scrollAnnouncementBar.style.position = 'fixed';
    this.scrollAnnouncementBar.style.top = '0';
    this.scrollAnnouncementBar.style.left = '0';
    this.scrollAnnouncementBar.style.right = '0';
    this.scrollAnnouncementBar.style.zIndex = '9999';
    this.scrollAnnouncementBar.classList.add('sticky');
    this.scrollAnnouncementBar.classList.remove('static');
    
    // Add padding to body to account for fixed bar
    this.adjustBodyPadding();
    
    console.log('Scroll announcement bar positioned as sticky at top');
  }

  /**
   * Apply static positioning - flows with page content
   */
  applyStaticPositioning() {
    this.scrollAnnouncementBar.style.position = 'relative';
    this.scrollAnnouncementBar.style.top = '';
    this.scrollAnnouncementBar.style.left = '';
    this.scrollAnnouncementBar.style.right = '';
    this.scrollAnnouncementBar.style.zIndex = '1000';
    this.scrollAnnouncementBar.classList.add('static');
    this.scrollAnnouncementBar.classList.remove('sticky');
    
    // Remove any body padding
    document.body.style.paddingTop = '';
    
    // Position at the beginning of body
    this.moveToBodyStart();
    
    console.log('Scroll announcement bar positioned as static');
  }

  /**
   * Hide the announcement bar
   */
  hideBar() {
    this.scrollAnnouncementBar.style.display = 'none';
    
    // Remove body padding if it was sticky
    document.body.style.paddingTop = '';
    
    console.log('Scroll announcement bar hidden');
  }

  /**
   * Adjust body padding for sticky positioning
   */
  adjustBodyPadding() {
    const barHeight = this.scrollAnnouncementBar.offsetHeight;
    document.body.style.paddingTop = barHeight + 'px';
  }

  /**
   * Move bar to the beginning of body
   */
  moveToBodyStart() {
    const body = document.body;
    const firstChild = body.firstChild;
    
    // Remove from current position if it exists
    if (this.scrollAnnouncementBar.parentNode) {
      this.scrollAnnouncementBar.parentNode.removeChild(this.scrollAnnouncementBar);
    }
    
    // Insert at the beginning of body
    if (firstChild) {
      body.insertBefore(this.scrollAnnouncementBar, firstChild);
    } else {
      body.appendChild(this.scrollAnnouncementBar);
    }
  }

  /**
   * Setup event listeners
   */
  setupEventListeners() {
    // Handle window resize for responsive behavior
    window.addEventListener('resize', () => this.handleResize());
    
    // Handle visibility change (when tab becomes active/inactive)
    document.addEventListener('visibilitychange', () => this.handleVisibilityChange());
  }

  /**
   * Setup gradient background based on settings
   */
  setupGradientBackground() {
    const hasGradient = getComputedStyle(document.documentElement)
      .getPropertyValue('--scroll-announcement-gradient-color').trim();
    
    if (hasGradient) {
      this.scrollAnnouncementBar.setAttribute('data-gradient', 'true');
    }
  }

  /**
   * Handle window resize
   */
  handleResize() {
    // Debounce resize events
    clearTimeout(this.resizeTimeout);
    this.resizeTimeout = setTimeout(() => {
      // Re-check visibility and positioning if needed
      this.adjustForScreenSize();
      
      // Adjust body padding only if sticky and should be visible
      const settings = this.getSettings();
      if (settings.sticky && settings.enabled && this.shouldShowOnCurrentPage(settings.showOnAllPages)) {
        this.adjustBodyPadding();
      }
    }, 250);
  }

  /**
   * Adjust scroll announcement bar for different screen sizes
   */
  adjustForScreenSize() {
    if (!this.scrollAnnouncementBar || this.scrollAnnouncementBar.style.display === 'none') {
      return;
    }

    const screenWidth = window.innerWidth;
    
    // Add classes for different screen sizes if needed
    if (screenWidth <= 768) {
      this.scrollAnnouncementBar.classList.add('mobile');
      this.scrollAnnouncementBar.classList.remove('tablet', 'desktop');
    } else if (screenWidth <= 1024) {
      this.scrollAnnouncementBar.classList.add('tablet');
      this.scrollAnnouncementBar.classList.remove('mobile', 'desktop');
    } else {
      this.scrollAnnouncementBar.classList.add('desktop');
      this.scrollAnnouncementBar.classList.remove('mobile', 'tablet');
    }
  }

  /**
   * Handle visibility change (tab active/inactive)
   */
  handleVisibilityChange() {
    if (!this.scrollAnnouncementBar) return;
    
    const content = this.scrollAnnouncementBar.querySelector('.scroll-announcement-bar-content');
    if (!content) return;
    
    if (document.hidden) {
      // Pause animation when tab is not visible
      content.style.animationPlayState = 'paused';
    } else {
      // Resume animation when tab becomes visible
      content.style.animationPlayState = 'running';
    }
  }

  /**
   * Public method to check if scroll announcement bar is visible
   */
  isVisible() {
    return this.scrollAnnouncementBar && this.scrollAnnouncementBar.style.display !== 'none';
  }

  /**
   * Public method to refresh positioning based on current settings
   */
  refresh() {
    const settings = this.getSettings();
    this.handlePositioning(settings);
  }
}

/**
 * Initialize scroll announcement bar when script loads
 */
(() => {
  // Create global instance
  window.ashluxeScrollAnnouncementBar = new ScrollAnnouncementBar();
  
  // Optional: Expose utility functions for theme developers
  window.ScrollAnnouncementBarUtils = {
    isVisible: () => window.ashluxeScrollAnnouncementBar.isVisible(),
    refresh: () => window.ashluxeScrollAnnouncementBar.refresh()
  };
})();

/**
 * Performance optimization: Preload critical resources
 */
if ('requestIdleCallback' in window) {
  requestIdleCallback(() => {
    // Preload any additional resources if needed
    console.log('Scroll announcement bar: Idle callback executed');
  });
}

/**
 * Export for module systems (if used)
 */
if (typeof module !== 'undefined' && module.exports) {
  module.exports = ScrollAnnouncementBar;
}

if (typeof define === 'function' && define.amd) {
  define([], () => ScrollAnnouncementBar);
}